import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Grid,
  Paper,
  Typography,
  makeStyles,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  Container,
  Tabs,
  Tab,
  Chip,
  Snackbar,
  Divider,
  TextField,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Drawer,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

// ─── Model column definitions ───────────────────────────────────
const MODEL_COLUMNS = {
  workTickets: {
    label: 'Work Tickets',
    modelKey: 'workTickets',
    columns: [
      { field: 'WorkTicketNumber', label: 'WT#', width: 100 },
      { field: 'WorkTicketStatusName', label: 'Status', width: 100 },
      { field: 'PropertyName', label: 'Property', width: 200 },
      { field: 'CrewLeaderName', label: 'Crew Leader', width: 150 },
      { field: 'ServiceName', label: 'Service', width: 150 },
      { field: 'ScheduledStartDate', label: 'Scheduled', width: 120, format: 'date' },
      { field: 'HoursEst', label: 'Est Hrs', width: 80 },
      { field: 'HoursAct', label: 'Act Hrs', width: 80 }
    ],
    defaultSort: 'ScheduledStartDate'
  },
  workTicketVisits: {
    label: 'Work Ticket Visits',
    modelKey: 'workTicketVisits',
    columns: [
      { field: 'WorkTicketNumber', label: 'WT#', width: 100 },
      { field: 'RouteName', label: 'Route', width: 150 },
      { field: 'ScheduledDate', label: 'Scheduled', width: 120, format: 'date' },
      { field: 'SequenceNum', label: 'Seq', width: 60 },
      { field: 'Hours', label: 'Hours', width: 80 },
      { field: 'RouteID', label: 'Route ID', width: 90 },
      { field: 'WorkTicketID', label: 'WT ID', width: 90 }
    ],
    defaultSort: 'ScheduledDate'
  },
  routes: {
    label: 'Routes',
    modelKey: 'routes',
    columns: [
      { field: 'RouteID', label: 'Route ID', width: 90 },
      { field: 'RouteName', label: 'Name', width: 200 },
      { field: 'CrewLeaderName', label: 'Crew Leader', width: 150 },
      { field: 'DivisionName', label: 'Division', width: 120 },
      { field: 'RouteSize', label: 'Size', width: 60 },
      { field: 'IsActive', label: 'Active', width: 70, format: 'bool' }
    ],
    defaultSort: 'RouteName'
  },
  routingProperties: {
    label: 'Routing Properties',
    modelKey: 'routingProperties',
    columns: [
      { field: 'propertyId', label: 'Prop ID', width: 80 },
      { field: 'name', label: 'Name', width: 200 },
      { field: 'address1', label: 'Address', width: 200 },
      { field: 'futureTicketCount', label: 'Future Visits', width: 100 },
      { field: 'netProfit', label: 'Net Profit', width: 100, format: 'currency' },
      { field: 'profitMargin', label: 'Margin', width: 80, format: 'pct' }
    ],
    defaultSort: 'name'
  },
  dailyRoutes: {
    label: 'Daily Routes',
    modelKey: 'dailyRoutes',
    columns: [
      { field: 'date', label: 'Date', width: 110, format: 'date' },
      { field: 'crewLeaderName', label: 'Crew Leader', width: 150 },
      { field: 'totalStops', label: 'Stops', width: 60 },
      { field: 'totalServiceTime', label: 'Svc Time', width: 80 },
      { field: 'totalDriveTime', label: 'Drive Time', width: 80 },
      { field: 'totalMiles', label: 'Miles', width: 70 },
      { field: 'isOptimized', label: 'Optimized', width: 80, format: 'bool' }
    ],
    defaultSort: 'date'
  },
  companies: {
    label: 'Companies',
    modelKey: 'companies',
    columns: [
      { field: 'CompanyID', label: 'ID', width: 70 },
      { field: 'CompanyName', label: 'Name', width: 250 },
      { field: 'Active', label: 'Active', width: 70, format: 'bool' },
      { field: 'matchStatus', label: 'Match', width: 100 },
      { field: 'lastSyncedAt', label: 'Last Synced', width: 130, format: 'date' }
    ],
    defaultSort: 'CompanyName'
  },
  contacts: {
    label: 'Contacts',
    modelKey: 'contacts',
    columns: [
      { field: 'ContactID', label: 'ID', width: 70 },
      { field: 'ContactName', label: 'Name', width: 200 },
      { field: 'CompanyName', label: 'Company', width: 150 },
      { field: 'RoleName', label: 'Role', width: 120 },
      { field: 'Email', label: 'Email', width: 180 },
      { field: 'IsActive', label: 'Active', width: 70, format: 'bool' }
    ],
    defaultSort: 'ContactName'
  },
  properties: {
    label: 'Properties',
    modelKey: 'properties',
    columns: [
      { field: 'PropertyID', label: 'ID', width: 70 },
      { field: 'PropertyName', label: 'Name', width: 200 },
      { field: 'PropertyAddressLine1', label: 'Address', width: 200 },
      { field: 'PropertyAddressCity', label: 'City', width: 120 },
      { field: 'matchStatus', label: 'Match', width: 100 }
    ],
    defaultSort: 'PropertyName'
  },
  catalogItems: {
    label: 'Catalog Items',
    modelKey: 'catalogItems',
    columns: [
      { field: 'CatalogItemID', label: 'ID', width: 70 },
      { field: 'ItemName', label: 'Name', width: 250 },
      { field: 'ItemCode', label: 'Code', width: 100 },
      { field: 'CategoryName', label: 'Category', width: 150 },
      { field: 'Price', label: 'Price', width: 80, format: 'currency' },
      { field: 'IsActive', label: 'Active', width: 70, format: 'bool' }
    ],
    defaultSort: 'ItemName'
  }
};

// ─── Styles ─────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
  },
  headerTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  statsContainer: {
    marginBottom: theme.spacing(3)
  },
  statCard: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8]
    }
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main
  },
  syncSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: 'white'
  },
  actionButton: {
    margin: theme.spacing(1),
    minWidth: 180
  },
  lockBanner: {
    marginBottom: theme.spacing(2)
  },
  lastSynced: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.5)
  },
  resultSection: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    maxHeight: 400,
    overflow: 'auto'
  },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1)
  },
  // Modal styles
  modalContent: {
    display: 'flex',
    flexDirection: 'row',
    height: '75vh',
    minHeight: 500,
    position: 'relative'
  },
  modalTableSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'flex 0.3s ease'
  },
  modalTableSideWithDetail: {
    flex: '0 0 55%'
  },
  modalSearchBar: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  modalTableWrap: {
    flex: 1,
    overflow: 'auto'
  },
  tableRowClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  tableRowSelected: {
    backgroundColor: theme.palette.action.selected + ' !important'
  },
  detailPanel: {
    flex: '0 0 45%',
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflow: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#fafafa'
  },
  detailField: {
    marginBottom: theme.spacing(1.5)
  },
  detailLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  detailValue: {
    fontSize: '0.9rem',
    wordBreak: 'break-all'
  }
}));

// ─── Helpers ────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(ms) {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  const sec = (ms / 1000).toFixed(1);
  if (sec < 60) return `${sec}s`;
  const min = (ms / 60000).toFixed(1);
  return `${min}m`;
}

function formatCellValue(value, format) {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'date':
      return new Date(value).toLocaleDateString();
    case 'bool':
      return value ? 'Yes' : 'No';
    case 'currency':
      return typeof value === 'number' ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value;
    case 'pct':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : value;
    default:
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}

// ─── Detail Panel Sub-component ─────────────────────────────────
const DetailPanel = ({ doc, onClose, classes }) => {
  if (!doc) return null;

  // Sort keys: IDs first, then alpha, _id/timestamps last
  const entries = Object.entries(doc).filter(([k]) => k !== '__v');
  const idKeys = entries.filter(([k]) => k.endsWith('ID') || k === 'propertyId' || k === '_id');
  const metaKeys = entries.filter(([k]) => k === 'lastSyncedAt' || k === 'createdAt' || k === 'updatedAt' || k === 'created_at' || k === 'updated_at');
  const rest = entries.filter(([k]) => !idKeys.find(([ik]) => ik === k) && !metaKeys.find(([mk]) => mk === k));

  const orderedEntries = [...idKeys, ...rest, ...metaKeys];

  return (
    <div className={classes.detailPanel}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" style={{ fontSize: '1rem' }}>Document Detail</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider style={{ marginBottom: 12 }} />
      {orderedEntries.map(([key, value]) => {
        let display;
        if (value === null || value === undefined) {
          display = <span style={{ color: '#999' }}>null</span>;
        } else if (typeof value === 'boolean') {
          display = value ? <Chip size="small" label="true" style={{ backgroundColor: '#4caf50', color: 'white' }} /> : <Chip size="small" label="false" variant="outlined" />;
        } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key === 'lastSyncedAt' || key === 'createdAt' || key === 'updatedAt' || key === 'created_at' || key === 'updated_at') {
          const d = new Date(value);
          display = isNaN(d.getTime()) ? String(value) : d.toLocaleString();
        } else if (Array.isArray(value)) {
          display = (
            <Collapse in={true}>
              <pre style={{ fontSize: '0.75rem', background: '#f0f0f0', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            </Collapse>
          );
        } else if (typeof value === 'object') {
          display = (
            <pre style={{ fontSize: '0.75rem', background: '#f0f0f0', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 150 }}>
              {JSON.stringify(value, null, 2)}
            </pre>
          );
        } else {
          display = String(value);
        }

        return (
          <div key={key} className={classes.detailField}>
            <Typography className={classes.detailLabel}>{key}</Typography>
            <Typography className={classes.detailValue} component="div">{display}</Typography>
          </div>
        );
      })}
    </div>
  );
};

// ─── Browse Modal Sub-component ─────────────────────────────────
const BrowseModal = ({ open, onClose, modelConfig, classes }) => {
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Find the first date column for this model (used as the dateField param)
  const dateColumns = modelConfig?.columns?.filter(c => c.format === 'date') || [];
  const primaryDateCol = dateColumns[0] || null;
  const [activeDateField, setActiveDateField] = useState('');

  const effectiveSort = sortField || modelConfig?.defaultSort || 'lastSyncedAt';

  const fetchDocs = useCallback(async () => {
    if (!modelConfig) return;
    setLoadingDocs(true);
    try {
      const params = {
        search,
        page: page + 1,
        limit: rowsPerPage,
        sort: effectiveSort,
        order: sortOrder
      };
      const dateFieldToUse = activeDateField || primaryDateCol?.field;
      if (dateFieldToUse && (dateFrom || dateTo)) {
        params.dateField = dateFieldToUse;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      }
      const { data } = await axios.get(`/api/aspire/routing/browse/${modelConfig.modelKey}`, { params });
      setDocs(data.docs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Browse fetch error:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [modelConfig, search, page, rowsPerPage, effectiveSort, sortOrder, dateFrom, dateTo, activeDateField, primaryDateCol]);

  useEffect(() => {
    if (open && modelConfig) {
      setDocs([]);
      setTotal(0);
      setPage(0);
      setSearch('');
      setSearchInput('');
      setSelectedDoc(null);
      setSortField(null);
      setSortOrder('desc');
      setDateFrom('');
      setDateTo('');
      setActiveDateField('');
    }
  }, [open, modelConfig]);

  useEffect(() => {
    if (open && modelConfig) {
      fetchDocs();
    }
  }, [open, modelConfig, fetchDocs]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleDateFilter = () => {
    setPage(0);
    // fetchDocs will pick up dateFrom/dateTo from state
    fetchDocs();
  };

  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setActiveDateField('');
    setPage(0);
  };

  if (!modelConfig) return null;

  const sortIndicator = (field) => {
    if (sortField !== field && effectiveSort !== field) return null;
    if (sortField === field || (!sortField && effectiveSort === field)) {
      return <span style={{ fontSize: '0.7rem', marginLeft: 2 }}>{sortOrder === 'asc' ? ' \u25B2' : ' \u25BC'}</span>;
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ style: { height: '85vh' } }}>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {modelConfig.label}
            <Chip label={`${total.toLocaleString()} docs`} size="small" style={{ marginLeft: 8 }} />
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div className={classes.modalContent}>
          {/* Left: Table */}
          <div className={`${classes.modalTableSide} ${selectedDoc ? classes.modalTableSideWithDetail : ''}`}>
            {/* Search + Date Range Bar */}
            <div className={classes.modalSearchBar}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search..."
                style={{ flex: 1, minWidth: 120 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                  )
                }}
              />
              <Button variant="contained" color="primary" size="small" onClick={handleSearch} disabled={loadingDocs}>
                Search
              </Button>
              {dateColumns.length > 0 && (
                <>
                  <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
                  {dateColumns.length > 1 && (
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={activeDateField || primaryDateCol?.field || ''}
                      onChange={(e) => setActiveDateField(e.target.value)}
                      style={{ minWidth: 100 }}
                      SelectProps={{ native: true }}
                    >
                      {dateColumns.map(dc => (
                        <option key={dc.field} value={dc.field}>{dc.label}</option>
                      ))}
                    </TextField>
                  )}
                  {dateColumns.length === 1 && (
                    <Typography variant="caption" color="textSecondary" style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}>
                      {primaryDateCol.label}:
                    </Typography>
                  )}
                  <TextField
                    size="small"
                    variant="outlined"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{ width: 145 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                    placeholder="From"
                  />
                  <Typography variant="caption" style={{ alignSelf: 'center' }}>–</Typography>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{ width: 145 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                    placeholder="To"
                  />
                  {(dateFrom || dateTo) && (
                    <IconButton size="small" onClick={clearDateFilter} title="Clear date filter">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
            </div>

            {loadingDocs && <Box textAlign="center" p={2}><CircularProgress size={24} /></Box>}

            {/* Table */}
            <div className={classes.modalTableWrap}>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {modelConfig.columns.map((col) => (
                        <TableCell
                          key={col.field}
                          style={{ fontWeight: 600, width: col.width, minWidth: col.width, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort(col.field)}
                        >
                          {col.label}{sortIndicator(col.field)}
                        </TableCell>
                      ))}
                      <TableCell style={{ width: 30 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docs.map((doc) => (
                      <TableRow
                        key={doc._id}
                        className={`${classes.tableRowClickable} ${selectedDoc?._id === doc._id ? classes.tableRowSelected : ''}`}
                        onClick={() => setSelectedDoc(doc)}
                      >
                        {modelConfig.columns.map((col) => (
                          <TableCell key={col.field} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: col.width }}>
                            {formatCellValue(doc[col.field], col.format)}
                          </TableCell>
                        ))}
                        <TableCell padding="none">
                          <ChevronRightIcon fontSize="small" color="action" />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loadingDocs && docs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={modelConfig.columns.length + 1} align="center" style={{ padding: 40 }}>
                          <Typography color="textSecondary">No documents found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onChangePage={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>

          {/* Right: Detail panel */}
          {selectedDoc && (
            <DetailPanel doc={selectedDoc} onClose={() => setSelectedDoc(null)} classes={classes} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────
const CronSyncDashboard = () => {
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [runningAction, setRunningAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastResult, setLastResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // Custom sync fields
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [fullBackfill, setFullBackfill] = useState(false);

  // Browse modal
  const [browseModel, setBrowseModel] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/aspire/routing/cron-status');
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch cron status:', err);
      setSnackbar({ open: true, message: 'Failed to load status', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const triggerAction = async (action, params) => {
    if (lastResult?._retryAction === action && !params?.force) {
      params = { ...params, force: true };
    }
    setRunningAction(action);
    try {
      const { data } = await axios.post('/api/aspire/routing/trigger-sync', { action, params });
      setLastResult(data);
      setShowResult(true);

      if (data.skipped) {
        if (data.reason === 'another_instance_running' && !params?.force) {
          setSnackbar({
            open: true,
            message: `Lock held — click again to force (lock: ${data.lockKey || 'unknown'})`,
            severity: 'warning'
          });
          setLastResult({ ...data, _retryAction: action, _retryParams: params });
        } else {
          setSnackbar({
            open: true,
            message: data.reason === 'local_dev_blocked'
              ? 'Sync blocked on local dev'
              : 'Skipped: ' + (data.reason || 'unknown'),
            severity: 'warning'
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: `${action} completed in ${formatDuration(data.duration)}`,
          severity: 'success'
        });
      }
      fetchStatus();
    } catch (err) {
      console.error(`Sync action ${action} failed:`, err);
      setSnackbar({
        open: true,
        message: `${action} failed: ${err.response?.data?.error || err.message}`,
        severity: 'error'
      });
    } finally {
      setRunningAction(null);
    }
  };

  const isRunning = runningAction !== null;

  // Map stat card keys to MODEL_COLUMNS keys for click-to-browse
  const statCards = status ? [
    {
      label: 'Work Tickets',
      value: status.workTickets?.total || 0,
      lastSynced: status.workTickets?.lastSyncedAt,
      browseKey: 'workTickets',
      chips: [
        { label: `Open: ${status.workTickets?.open || 0}`, color: '#ff9800' },
        { label: `Scheduled: ${status.workTickets?.scheduled || 0}`, color: '#2196f3' },
        { label: `Complete: ${status.workTickets?.complete || 0}`, color: '#4caf50' }
      ]
    },
    {
      label: 'Visits',
      value: status.workTicketVisits?.total || 0,
      lastSynced: status.workTicketVisits?.lastSyncedAt,
      browseKey: 'workTicketVisits',
      subtitle: status.workTicketVisits?.oldestDate && status.workTicketVisits?.newestDate
        ? `${new Date(status.workTicketVisits.oldestDate).toLocaleDateString()} → ${new Date(status.workTicketVisits.newestDate).toLocaleDateString()}`
        : null,
      chips: [
        { label: `Future: ${status.workTicketVisits?.future || 0}`, color: '#2196f3' }
      ]
    },
    {
      label: 'Routes (Active)',
      value: status.routes?.active || 0,
      lastSynced: status.routes?.lastSyncedAt,
      browseKey: 'routes'
    },
    {
      label: 'Routing Properties',
      value: status.routingProperties?.totalProperties || 0,
      lastSynced: status.routingProperties?.lastSyncedAt,
      browseKey: 'routingProperties',
      chips: status.routingProperties?.problemProperties
        ? [{ label: `Problems: ${status.routingProperties.problemProperties}`, color: '#f44336' }]
        : []
    },
    {
      label: 'Daily Routes',
      value: status.dailyRoutes?.total || 0,
      browseKey: 'dailyRoutes',
      subtitle: status.dailyRoutes?.lastCalcDate
        ? `Last calc: ${new Date(status.dailyRoutes.lastCalcDate).toLocaleDateString()}`
        : null
    },
    {
      label: 'Companies',
      value: status.companies?.total || 0,
      lastSynced: status.companies?.lastSyncedAt,
      browseKey: 'companies'
    },
    {
      label: 'Contacts',
      value: status.contacts?.total || 0,
      lastSynced: status.contacts?.lastSyncedAt,
      browseKey: 'contacts'
    },
    {
      label: 'Properties',
      value: status.properties?.total || 0,
      lastSynced: status.properties?.lastSyncedAt,
      browseKey: 'properties'
    },
    {
      label: 'Catalog Items',
      value: status.catalogItems?.total || 0,
      lastSynced: status.catalogItems?.lastSyncedAt,
      browseKey: 'catalogItems'
    }
  ] : [];

  const quickActions = [
    { action: 'daily-sync', label: 'Run Daily Sync', description: 'Full pipeline: routes, companies, contacts, properties, catalog, stale tickets, visits, rollup' },
    { action: 'hourly-sync', label: 'Run Hourly Sync', description: "Today's work tickets, visits, clock times, work ticket times" },
    { action: 'sync-visits-30day', label: 'Sync Visits 30-Day', description: 'Fetch next 30 days of visit data' },
    { action: 'sync-visits-90day', label: 'Sync Visits 90-Day', description: 'Fetch next 90 days of visit data' },
    { action: 'sync-visits-full-year', label: 'Sync Visits Full Year', description: 'Fetch remainder of current year' },
    { action: 'rebuild-future-visits', label: 'Rebuild Future Visits', description: 'Rollup visit data to RoutingProperty.futureVisits' }
  ];

  return (
    <Container maxWidth="xl" className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h4" className={classes.headerTitle}>
              Aspire Cron Sync Dashboard
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.9 }}>
              Monitor sync status and trigger manual syncs — click any card to browse data
            </Typography>
          </div>
          <Button
            variant="outlined"
            style={{ color: 'white', borderColor: 'white' }}
            startIcon={loading ? <CircularProgress size={18} style={{ color: 'white' }} /> : <RefreshIcon />}
            onClick={fetchStatus}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Active Locks Banner */}
      {status?.activeLocks?.length > 0 && (
        <Alert severity="warning" icon={<LockIcon />} className={classes.lockBanner}>
          <strong>Active Locks ({status.activeLocks.length}):</strong>{' '}
          {status.activeLocks.map((lock, i) => (
            <span key={i}>
              {lock._id} (owner: {lock.owner}, expires: {timeAgo(lock.expiresAt)})
              {i < status.activeLocks.length - 1 ? ' | ' : ''}
            </span>
          ))}
        </Alert>
      )}

      {/* Stat Cards — clickable to browse */}
      <Grid container spacing={2} className={classes.statsContainer}>
        {statCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
            <Card
              className={classes.statCard}
              onClick={() => card.browseKey && setBrowseModel(MODEL_COLUMNS[card.browseKey])}
            >
              <CardContent>
                <Typography variant="body2" color="textSecondary">{card.label}</Typography>
                <Typography className={classes.statNumber}>{card.value.toLocaleString()}</Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="textSecondary">{card.subtitle}</Typography>
                )}
                {card.chips && card.chips.length > 0 && (
                  <div className={classes.chipRow}>
                    {card.chips.map((chip, ci) => (
                      <Chip
                        key={ci}
                        label={chip.label}
                        size="small"
                        style={{ backgroundColor: chip.color, color: 'white', fontSize: '0.7rem' }}
                      />
                    ))}
                  </div>
                )}
                {card.lastSynced && (
                  <Typography className={classes.lastSynced}>
                    Last synced: {timeAgo(card.lastSynced)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Sync Actions Panel */}
      <Paper className={classes.syncSection}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Quick Actions" />
          <Tab label="Custom Sync" />
        </Tabs>

        <Divider style={{ marginBottom: 16 }} />

        {activeTab === 0 && (
          <Box>
            <Grid container spacing={2}>
              {quickActions.map(({ action, label, description }) => (
                <Grid item xs={12} sm={6} md={4} key={action}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                    fullWidth
                    startIcon={
                      runningAction === action
                        ? <CircularProgress size={20} style={{ color: 'white' }} />
                        : <PlayArrowIcon />
                    }
                    onClick={() => triggerAction(action)}
                    disabled={isRunning}
                  >
                    {label}
                  </Button>
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', paddingLeft: 8 }}>
                    {description}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Custom Date Range Sync</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fullBackfill}
                      onChange={(e) => setFullBackfill(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: fullBackfill ? 600 : 400 }}>
                        Full Backfill
                      </Typography>
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', lineHeight: 1.2 }}>
                        {fullBackfill
                          ? 'Syncs ALL tickets + visits + futureVisits + DailyRoutes w/ ORS'
                          : 'Visits only (Schedule Board)'}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color={fullBackfill ? 'secondary' : 'primary'}
                  fullWidth
                  startIcon={
                    (runningAction === 'sync-visits-custom' || runningAction === 'backfill-sync')
                      ? <CircularProgress size={20} style={{ color: 'white' }} />
                      : <ScheduleIcon />
                  }
                  onClick={() => {
                    if (!customStartDate || !customEndDate) {
                      setSnackbar({ open: true, message: 'Start and end dates are required', severity: 'warning' });
                      return;
                    }
                    const action = fullBackfill ? 'backfill-sync' : 'sync-visits-custom';
                    triggerAction(action, {
                      startDate: `${customStartDate}T00:00:00Z`,
                      endDate: `${customEndDate}T23:59:59Z`
                    });
                  }}
                  disabled={isRunning || !customStartDate || !customEndDate}
                >
                  {fullBackfill ? 'Run Full Backfill' : 'Sync Visits'}
                </Button>
              </Grid>
            </Grid>

            <Divider style={{ margin: '24px 0' }} />

            <Typography variant="h6" gutterBottom>Resync Single Property</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Aspire Property ID"
                  type="number"
                  fullWidth
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  placeholder="e.g. 12345"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={
                    runningAction === 'resync-property'
                      ? <CircularProgress size={20} style={{ color: 'white' }} />
                      : <RefreshIcon />
                  }
                  onClick={() => {
                    const pid = parseInt(propertyId, 10);
                    if (!pid || isNaN(pid)) {
                      setSnackbar({ open: true, message: 'Enter a valid numeric property ID', severity: 'warning' });
                      return;
                    }
                    triggerAction('resync-property', { propertyId: pid });
                  }}
                  disabled={isRunning || !propertyId}
                >
                  Resync Property
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Last Sync Results */}
      {lastResult && (
        <Paper className={classes.syncSection}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Last Sync Result
              {lastResult.action && (
                <Chip
                  label={lastResult.action}
                  size="small"
                  color={lastResult.success ? 'primary' : 'secondary'}
                  style={{ marginLeft: 8 }}
                />
              )}
              {lastResult.duration && (
                <Chip
                  label={formatDuration(lastResult.duration)}
                  size="small"
                  variant="outlined"
                  style={{ marginLeft: 4 }}
                />
              )}
            </Typography>
            <IconButton onClick={() => setShowResult(!showResult)} size="small">
              {showResult ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showResult}>
            <Paper className={classes.resultSection} variant="outlined">
              <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </Paper>
          </Collapse>
        </Paper>
      )}

      {/* Browse Modal */}
      <BrowseModal
        open={!!browseModel}
        onClose={() => setBrowseModel(null)}
        modelConfig={browseModel}
        classes={classes}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  authenticated: state.authenticated
});

export default connect(mapStateToProps)(CronSyncDashboard);
